import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { 
  FileText, 
  Upload, 
  UploadCloud,
  Users, 
  ArrowLeft, 
  RefreshCw,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { BulkAnalysisUpload } from './BulkAnalysisUpload';
import { BulkAnalysisResults } from './BulkAnalysisResults';
import { BulkAnalysisDetails } from './BulkAnalysisDetails';
import type { BulkAnalysisResult } from './types/bulk';
import { UsageIndicator } from '../pricing/UsageIndicator';
import { UserActionsService } from '../../services/userActions';

interface BulkAnalysisPageProps {
  projectId?: string;
  projectRequirements?: {
    experience: string;
    skills: string[];
    education: string;
  };
  initialJobTitle?: string;
  initialJobDescription?: string;
  onBack?: () => void;
  onCandidateSelect?: (candidate: BulkAnalysisResult) => void;
  onAddToProject?: (candidate: BulkAnalysisResult) => void;
}

export function BulkAnalysisPage({
  projectId,
  projectRequirements,
  initialJobTitle,
  initialJobDescription,
  onBack,
  onCandidateSelect,
  onAddToProject
}: BulkAnalysisPageProps) {
  const [currentStep, setCurrentStep] = useState<'upload' | 'results' | 'details'>('upload');
  const [analysisResults, setAnalysisResults] = useState<BulkAnalysisResult[]>([]);
  const [selectedCandidate, setSelectedCandidate] = useState<BulkAnalysisResult | null>(null);
  const [isAnalysisComplete, setIsAnalysisComplete] = useState(false);
  // const { subscription, isLoading: subscriptionLoading } = useSubscription();

  // Track bulk analysis feature usage on mount
  useEffect(() => {
    const trackFeatureUsage = async () => {
      try {
        await UserActionsService.trackFeatureUsage('bulk-analysis', {
          project_id: projectId,
          accessed_at: new Date().toISOString()
        });
      } catch (error) {
        console.error('Error tracking bulk analysis usage:', error);
      }
    };

    trackFeatureUsage();
  }, [projectId]);

  const handleAnalysisComplete = async (results: BulkAnalysisResult[]) => {
    setAnalysisResults(results);
    setIsAnalysisComplete(true);
    setCurrentStep('results');

    // Track bulk analysis completion
    try {
      await UserActionsService.trackFeatureUsage('bulk-analysis-completed', {
        project_id: projectId,
        results_count: results.length,
        successful_analyses: results.filter(r => !r.error).length,
        completed_at: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error tracking bulk analysis completion:', error);
    }
  };

  const handleBackToUpload = () => {
    setCurrentStep('upload');
    setIsAnalysisComplete(false);
  };

  const handleStartNewAnalysis = () => {
    setAnalysisResults([]);
    setSelectedCandidate(null);
    setIsAnalysisComplete(false);
    setCurrentStep('upload');
  };

  const handleCandidateSelect = (candidate: BulkAnalysisResult) => {
    setSelectedCandidate(candidate);
    setCurrentStep('details');
    if (onCandidateSelect) {
      onCandidateSelect(candidate);
    }
  };

  const handleBackFromDetails = () => {
    setSelectedCandidate(null);
    setCurrentStep('results');
  };

  const handleExport = (format: 'csv' | 'pdf' | 'excel') => {
    // Generate export data based on format
    const exportData = analysisResults.map(result => ({
      candidateName: result.candidateName,
      fileName: result.fileName,
      overallScore: result.overallScore,
      technicalScore: result.technicalScore,
      experienceScore: result.experienceScore,
      culturalFitScore: result.culturalFitScore,
      verdict: result.verdict,
      matchedSkills: result.matchedSkills.join(', '),
      missingSkills: result.missingSkills.join(', '),
      strengths: result.strengths.join('; '),
      weaknesses: result.weaknesses.join('; '),
      analysisDate: result.analysisDate.toISOString(),
      processingTime: `${result.processingTime}ms`,
      error: result.error || ''
    }));

    if (format === 'csv') {
      // Convert to CSV
      const headers = Object.keys(exportData[0] || {});
      const csvContent = [
        headers.join(','),
        ...exportData.map(row => 
          headers.map(header => 
            `"${String(row[header as keyof typeof row]).replace(/"/g, '""')}"`
          ).join(',')
        )
      ].join('\n');

      // Download CSV
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `bulk-analysis-results-${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    
    // For PDF and Excel, you would implement similar logic
    // This is a simplified implementation for demonstration
    console.log(`Exporting ${analysisResults.length} results as ${format}`);
  };

  // Calculate summary stats for display
  const summaryStats = {
    totalCandidates: analysisResults.length,
    successfulAnalyses: analysisResults.filter(r => !r.error).length,
    averageScore: analysisResults.length > 0 
      ? analysisResults.filter(r => !r.error).reduce((sum, r) => sum + r.overallScore, 0) / analysisResults.filter(r => !r.error).length
      : 0,
    topCandidates: analysisResults.filter(r => !r.error && r.overallScore >= 8).length,
    strongFits: analysisResults.filter(r => r.verdict === 'strong_fit').length,
    totalProcessingTime: analysisResults.reduce((sum, r) => sum + r.processingTime, 0)
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="container mx-auto px-4 py-6 space-y-8">
        {/* Header */}
        <Card className="bg-gradient-to-br from-slate-50 to-slate-100/50 border-slate-200/60 rounded-2xl shadow-sm">
          <CardHeader className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 p-8">
            <div className="flex items-center gap-4">
              {onBack && (
                <Button variant="outline" onClick={onBack} className="rounded-xl">
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Back
                </Button>
              )}
              <div>
                <CardTitle className="text-2xl font-bold text-slate-900 flex items-center gap-3 mb-2 tracking-tight">
                  <div className="h-8 w-8 bg-gradient-to-br from-slate-800 to-slate-900 rounded-xl flex items-center justify-center shadow-lg">
                    <UploadCloud className="w-4 h-4 text-white" />
                  </div>
                  Bulk CV Analysis
                </CardTitle>
                <CardDescription className="text-slate-600">
                  {projectId 
                    ? "Analyze multiple candidates against your project requirements" 
                    : "Upload and analyze multiple CVs against a job description"
                  }
                </CardDescription>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <UsageIndicator />
              
              {isAnalysisComplete && currentStep === 'results' && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={handleBackToUpload} className="rounded-xl">
                    <Upload className="h-4 w-4 mr-2" />
                    New Analysis
                  </Button>
                  <Button onClick={handleStartNewAnalysis} className="rounded-xl">
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Start Over
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Project Context Banner */}
        {projectId && projectRequirements && (
          <Card className="border-blue-200 bg-blue-50 dark:bg-blue-900/20 dark:border-blue-800">
            <CardContent className="p-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <Users className="h-5 w-5 text-white" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-blue-900 dark:text-blue-100">
                    Project-Based Analysis
                  </h3>
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    Candidates will be analyzed against project requirements: {projectRequirements.experience} experience level
                  </p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {projectRequirements.skills.slice(0, 6).map((skill, index) => (
                      <Badge key={index} variant="outline" className="text-xs bg-blue-100 border-blue-300 text-blue-800">
                        {skill}
                      </Badge>
                    ))}
                    {projectRequirements.skills.length > 6 && (
                      <Badge variant="outline" className="text-xs bg-blue-100 border-blue-300 text-blue-800">
                        +{projectRequirements.skills.length - 6} more
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Steps */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                {/* Step 1: Upload */}
                <div className="flex items-center">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    ${currentStep === 'upload' 
                      ? 'bg-blue-500 text-white' 
                      : isAnalysisComplete 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }
                  `}>
                    {isAnalysisComplete ? <CheckCircle className="h-4 w-4" /> : '1'}
                  </div>
                  <span className="ml-2 text-sm font-medium">Upload & Configure</span>
                </div>

                {/* Separator */}
                <div className={`flex-1 h-0.5 ${isAnalysisComplete ? 'bg-green-500' : 'bg-gray-200'}`} />

                {/* Step 2: Results */}
                <div className="flex items-center">
                  <div className={`
                    w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium
                    ${currentStep === 'results' && isAnalysisComplete
                      ? 'bg-blue-500 text-white' 
                      : isAnalysisComplete 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-200 text-gray-600'
                    }
                  `}>
                    {isAnalysisComplete ? <CheckCircle className="h-4 w-4" /> : '2'}
                  </div>
                  <span className="ml-2 text-sm font-medium">View Results</span>
                </div>

                {/* Separator - only show if in details view */}
                {currentStep === 'details' && (
                  <>
                    <div className="flex-1 h-0.5 bg-green-500" />
                    
                    {/* Step 3: Details */}
                    <div className="flex items-center">
                      <div className="w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium bg-blue-500 text-white">
                        3
                      </div>
                      <span className="ml-2 text-sm font-medium">Candidate Details</span>
                    </div>
                  </>
                )}
              </div>

              {/* Summary Stats (when analysis is complete) */}
              {isAnalysisComplete && (
                <div className="flex items-center gap-6 text-sm">
                  <div className="text-center">
                    <div className="font-bold text-lg text-blue-600">{summaryStats.totalCandidates}</div>
                    <div className="text-gray-500">Analyzed</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-lg text-green-600">{summaryStats.strongFits}</div>
                    <div className="text-gray-500">Strong Fits</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-lg text-purple-600">{summaryStats.averageScore.toFixed(1)}</div>
                    <div className="text-gray-500">Avg Score</div>
                  </div>
                  <div className="text-center">
                    <div className="font-bold text-lg text-orange-600">{Math.round(summaryStats.totalProcessingTime / 1000)}s</div>
                    <div className="text-gray-500">Time</div>
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Subscription Check */}
        {false && (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              You've reached your analysis limit for this billing period. 
              <a href="/pricing" className="underline ml-1">Upgrade your plan</a> to continue analyzing CVs.
            </AlertDescription>
          </Alert>
        )}

        {/* Main Content */}
        <div className="space-y-6">
          {currentStep === 'upload' && (
            <BulkAnalysisUpload
              projectId={projectId}
              projectRequirements={projectRequirements}
              initialJobTitle={initialJobTitle}
              initialJobDescription={initialJobDescription}
              onAnalysisComplete={handleAnalysisComplete}
            />
          )}

          {currentStep === 'results' && isAnalysisComplete && (
            <div className="space-y-6">
              {/* Quick Actions */}
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                        <CheckCircle className="h-5 w-5 text-white" />
                      </div>
                      <div>
                        <h3 className="font-semibold text-green-900 dark:text-green-100">
                          Analysis Complete!
                        </h3>
                        <p className="text-sm text-green-700 dark:text-green-300">
                          Successfully analyzed {summaryStats.successfulAnalyses} out of {summaryStats.totalCandidates} candidates
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2">
                      <Button variant="outline" onClick={() => handleExport('csv')}>
                        <FileText className="h-4 w-4 mr-2" />
                        Export CSV
                      </Button>
                      <Button onClick={handleBackToUpload}>
                        <Upload className="h-4 w-4 mr-2" />
                        Analyze More
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Results Component */}
              <BulkAnalysisResults
                results={analysisResults}
                onCandidateSelect={handleCandidateSelect}
                onAddToProject={onAddToProject}
                onExport={handleExport}
                projectId={projectId}
              />
            </div>
          )}

          {currentStep === 'details' && selectedCandidate && (
            <BulkAnalysisDetails
              candidate={selectedCandidate}
              onBack={handleBackFromDetails}
              onAddToProject={onAddToProject}
              projectId={projectId}
            />
          )}
        </div>


      </div>
    </div>
  );
} 